import { mapValues } from 'lodash';
import type { Path } from 'react-router-dom';

interface RouteParamsWithBookingNo {
  bookingNo: string | number;
}

export const routeCreators = {
  bookings: () => `/bookings`,
  callback: () => `/callback`,
  claimsPortal: () => `/claims`,
  noShowLateHandling: () => `/noshow-latehandling`,
  financialDocuments: () => `/financial-documents`,
  bookingClaims: ({ bookingNo }: RouteParamsWithBookingNo) => `/claims/${bookingNo}`,
  help: () => '/help',
  create: () => '/create',
  customsDocumentation: ({
    bookingNo,
    route,
    sailingId,
    userId,
  }: RouteParamsWithBookingNo & { sailingId: string; userId: string; route: string }) =>
    `/customs-documentation/${bookingNo}/${sailingId}/${userId}/${route}`,
  hazardousGoods: ({ bookingNo }: RouteParamsWithBookingNo) =>
    `/bookings/${bookingNo}/hazardous-goods`,
  settings: () => '/settings',
  settingsPassword: () => '/settings/password',
  settingsFilters: () => '/settings/filters',
  settingsFavourites: () => '/settings/favourites',
  settingsDateFormat: () => '/settings/date-format',
  settingsCookies: () => '/settings/cookies',
  userSubscriptions: () => '/settings/notifications',
  payment: () => '/pay',
  timetable: () => '/timetable',
  statistics: () => '/statistics',
  intermodal: () => '/intermodal',
};

export interface Location extends Partial<Path> {
  pathname: string;
}

export const MODAL_LOCATION_TYPE = Symbol('ModalLocation');

export interface ModalLocation {
  path: Location;
  type: typeof MODAL_LOCATION_TYPE;
}

export const modalRouteCreators = {
  bookingDetailsView: ({ bookingNo }: RouteParamsWithBookingNo): ModalLocation => ({
    path: { pathname: `/bookings/${bookingNo}/view` },
    type: MODAL_LOCATION_TYPE,
  }),
  bookingDetailsEdit: ({ bookingNo }: RouteParamsWithBookingNo): ModalLocation => ({
    path: { pathname: `/bookings/${bookingNo}/edit` },
    type: MODAL_LOCATION_TYPE,
  }),
  bookingSwitch: ({ bookingNo }: RouteParamsWithBookingNo): ModalLocation => ({
    path: { pathname: `/bookings/${bookingNo}/switch` },
    type: MODAL_LOCATION_TYPE,
  }),
  bookingCancel: ({ bookingNo }: RouteParamsWithBookingNo): ModalLocation => ({
    path: { pathname: `/bookings/${bookingNo}/cancel` },
    type: MODAL_LOCATION_TYPE,
  }),
  intermodalDetailsEdit: ({ bookingNo }: RouteParamsWithBookingNo & {}): ModalLocation => ({
    path: {
      pathname: `/intermodal/${bookingNo}`,
    },
    type: MODAL_LOCATION_TYPE,
  }),
  intermodalCreate: (): ModalLocation => ({
    path: {
      pathname: `/intermodal/create`,
    },
    type: MODAL_LOCATION_TYPE,
  }),
};

export const getPayBookingPath = (bookingNos: number[]) =>
  routeCreators.payment() + '?bookingNo=' + bookingNos.join(',');

export const modalRouteLocations = {
  bookingSwitch: modalRouteCreators.bookingSwitch({ bookingNo: ':bookingNo' }),
  bookingCancel: modalRouteCreators.bookingCancel({ bookingNo: ':bookingNo' }),
  intermodalCreate: modalRouteCreators.intermodalCreate(),
  intermodalDetailsEdit: modalRouteCreators.intermodalDetailsEdit({ bookingNo: ':bookingNo' }),
  bookingDetailsEdit: modalRouteCreators.bookingDetailsEdit({
    bookingNo: ':bookingNo',
  }),
  bookingDetailsView: modalRouteCreators.bookingDetailsView({
    bookingNo: ':bookingNo',
  }),
};

export const appRoutes = {
  ...mapValues(modalRouteLocations, route => route.path.pathname),
  bookings: routeCreators.bookings() + '/*',
  callback: routeCreators.callback(),
  claimsPortal: routeCreators.claimsPortal(),
  bookingClaims: routeCreators.bookingClaims({ bookingNo: ':bookingNo' }),
  financialDocuments: routeCreators.financialDocuments() + '/*',
  help: routeCreators.help(),
  intermodal: routeCreators.intermodal() + '/*',
  intermodalCreate: modalRouteCreators.intermodalCreate().path.pathname,
  create: routeCreators.create(),
  settings: routeCreators.settings() + '/*',
  settingsPassword: routeCreators.settingsPassword(),
  settingsFilters: routeCreators.settingsFilters(),
  settingsFavourites: routeCreators.settingsFavourites(),
  settingsDateFormat: routeCreators.settingsDateFormat(),
  settingsCookies: routeCreators.settingsCookies(),
  userSubscriptions: routeCreators.userSubscriptions(),
  payment: routeCreators.payment(),
  timetable: routeCreators.timetable(),
  hazardousGoods: routeCreators.hazardousGoods({ bookingNo: ':bookingNo' }),
  customsDocumentation: routeCreators.customsDocumentation({
    bookingNo: ':bookingNo',
    sailingId: ':sailingId',
    userId: ':userId',
    route: ':route',
  }),
  statistics: routeCreators.statistics(),
  noShowLateHandling: routeCreators.noShowLateHandling(),
};

export type AppRoutes = typeof appRoutes;
export type ModalRouteLocations = typeof modalRouteLocations;
